import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../../utils/api';
import './FaturamentoMensalReport.css';

interface Venda {
  id?: number;
  data_venda?: string;
  valor_total?: number;
  cliente?: string;
  produto?: string;
  quantidade?: number;
  grupo?: string;
  cte?: number;
  status?: string;
  bloqueio_faturamento?: number;
}

interface FiltroPeriodo {
  dataInicio: string;
  dataFim: string;
}

interface FaturamentoReportProps {
  dashboardId: string;
  dashboardTitle: string;
}

interface GrupoResumo {
  grupo_pbi: string;
  qtd_ctes?: number;
  faturamento?: number;
}

interface ResumoMensalPbi {
  kpis?: {
    meta_faturamento?: number;
    faturamento_total?: number;
    nfs_emitidas?: number;
    faturamento_cte?: number;
    faturamento_frota?: number;
    faturamento_terceiros?: number;
    qtd_ctes_validos?: number;
    qtd_nfs?: number;
  };
  grupos?: GrupoResumo[];
}

const MAIN_GROUPS = ['BROSE', 'ELDORADO', 'VOLKSWAGEN'];

const MONTHS = [
  { value: 0, label: 'janeiro' },
  { value: 1, label: 'fevereiro' },
  { value: 2, label: 'marco' },
  { value: 3, label: 'abril' },
  { value: 4, label: 'maio' },
  { value: 5, label: 'junho' },
  { value: 6, label: 'julho' },
  { value: 7, label: 'agosto' },
  { value: 8, label: 'setembro' },
  { value: 9, label: 'outubro' },
  { value: 10, label: 'novembro' },
  { value: 11, label: 'dezembro' },
];

const toDateParam = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const getMonthPeriod = (year: number, month: number): FiltroPeriodo => {
  const start = new Date(year, month, 1);
  const end = new Date(year, month + 1, 0);
  return {
    dataInicio: toDateParam(start),
    dataFim: toDateParam(end),
  };
};

const parseValor = (value: unknown) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeGroup = (value?: string) => {
  const text = String(value || '').trim().toUpperCase();
  return text || 'OUTROS';
};

const getDateParts = (value?: string) => {
  if (!value) return null;
  const text = String(value).slice(0, 10);
  const [year, month, day] = text.split('-').map(Number);
  if (!year || !month || !day) return null;
  return { year, month, day };
};

const formatNumber = (value: number) => new Intl.NumberFormat('pt-BR').format(value);

const formatMillions = (value: number) => {
  const millions = value / 1000000;
  return `${new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: millions >= 10 ? 1 : 3,
    maximumFractionDigits: millions >= 10 ? 1 : 3,
  }).format(millions)} Mi`;
};

const formatCompactMoney = (value: number) => `R$ ${formatMillions(value)}`;

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(value);
};

const formatTooltipMoney = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatPercent = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('pt-BR');
};

const exportCsv = (rows: Venda[]) => {
  const headers = ['Data', 'Grupo', 'Cliente', 'CTE', 'Status', 'Valor Total'];
  const escape = (value: unknown) => {
    const text = String(value ?? '').replace(/"/g, '""').replace(/\r?\n/g, ' ');
    return `"${text}"`;
  };

  const content = [
    '\ufeff',
    headers.map(escape).join(';'),
    ...rows.map((row) => [
      formatDate(row.data_venda),
      normalizeGroup(row.grupo),
      row.cliente || '-',
      row.cte || '-',
      row.status || '-',
      parseValor(row.valor_total).toFixed(2),
    ].map(escape).join(';')),
  ].join('\n');

  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `faturamento_mensal_${Date.now()}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const FaturamentoMensalReport: React.FC<FaturamentoReportProps> = ({ dashboardTitle }) => {
  const now = new Date();
  const [anoFiltro, setAnoFiltro] = useState(now.getFullYear());
  const [mesFiltro, setMesFiltro] = useState(now.getMonth());
  const [periodo, setPeriodo] = useState<FiltroPeriodo>(() => getMonthPeriod(now.getFullYear(), now.getMonth()));
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [vendas, setVendas] = useState<Venda[]>([]);
  const [vendasMesAnterior, setVendasMesAnterior] = useState<Venda[]>([]);
  const [resumoPbi, setResumoPbi] = useState<ResumoMensalPbi | null>(null);
  const [gruposSelecionados, setGruposSelecionados] = useState<Set<string>>(() => new Set(MAIN_GROUPS));
  const [hoveredDay, setHoveredDay] = useState<number | null>(null);

  const carregar = async () => {
    setLoading(true);
    setErro('');

    try {
      const params = {
        dataInicio: periodo.dataInicio,
        dataFim: periodo.dataFim,
      };

      const responseVendas = await api.get('/vendas', { params });
      setVendas(Array.isArray(responseVendas.data?.data) ? responseVendas.data.data : []);

      const previousMonthStart = new Date(anoFiltro, mesFiltro - 1, 1);
      const previousPeriod = getMonthPeriod(previousMonthStart.getFullYear(), previousMonthStart.getMonth());
      const responseVendasMesAnterior = await api.get('/vendas', { params: previousPeriod });
      setVendasMesAnterior(
        Array.isArray(responseVendasMesAnterior.data?.data) ? responseVendasMesAnterior.data.data : []
      );

      const responseResumo = await api.get('/faturamento-mensal-resumo', { params });
      setResumoPbi(responseResumo.data || null);
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Nao foi possivel carregar o faturamento no momento.';
      setErro(message);
      setVendas([]);
      setVendasMesAnterior([]);
      setResumoPbi(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPeriodo(getMonthPeriod(anoFiltro, mesFiltro));
  }, [anoFiltro, mesFiltro]);

  const years = useMemo(() => {
    const current = new Date().getFullYear();
    const set = new Set([current - 1, current, current + 1, anoFiltro]);
    vendas.forEach(item => {
      const parts = getDateParts(item.data_venda);
      if (parts?.year) set.add(parts.year);
    });
    return Array.from(set).sort((a, b) => b - a);
  }, [anoFiltro, vendas]);

  const vendasFiltradas = useMemo(() => {
    return vendas.filter(item => {
      const grupo = normalizeGroup(item.grupo);
      return gruposSelecionados.has(grupo);
    });
  }, [vendas, gruposSelecionados]);

  const vendasMesAnteriorFiltradas = useMemo(() => {
    return vendasMesAnterior.filter(item => {
      const grupo = normalizeGroup(item.grupo);
      return gruposSelecionados.has(grupo);
    });
  }, [vendasMesAnterior, gruposSelecionados]);

  const resumo = useMemo(() => {
    const totalFaturamento = vendasFiltradas.reduce((acc, item) => acc + parseValor(item.valor_total), 0);
    const totalVendas = vendasFiltradas.length;
    const clientesUnicos = new Set(vendasFiltradas.map(item => String(item.cliente || '').trim()).filter(Boolean)).size;
    const bloqueados = vendasFiltradas.filter(item => Number(item.bloqueio_faturamento || 0) === 1).length;

    return {
      totalFaturamento,
      totalVendas,
      clientesUnicos,
      bloqueados,
    };
  }, [vendasFiltradas]);

  const gruposPbi = useMemo(() => {
    const grupos = Array.isArray(resumoPbi?.grupos) ? resumoPbi.grupos : [];
    return [...grupos]
      .map(item => ({ ...item, grupo_pbi: normalizeGroup(item.grupo_pbi) }))
      .sort((a, b) => parseValor(b.faturamento) - parseValor(a.faturamento));
  }, [resumoPbi]);

  const kpisPbi = useMemo(() => {
    const k = resumoPbi?.kpis;
    return {
      metaFaturamento: parseValor(k?.meta_faturamento),
      faturamentoTotal: parseValor(k?.faturamento_total),
      nfsEmitidas: parseValor(k?.nfs_emitidas),
      faturamentoCte: parseValor(k?.faturamento_cte),
      faturamentoFrota: parseValor(k?.faturamento_frota),
      faturamentoTerceiros: parseValor(k?.faturamento_terceiros),
    };
  }, [resumoPbi]);

  const todosGruposSelecionados = useMemo(() => {
    return MAIN_GROUPS.every(group => gruposSelecionados.has(group));
  }, [gruposSelecionados]);

  const kpisFiltrados = useMemo(() => {
    const nfsEmitidas = todosGruposSelecionados ? kpisPbi.nfsEmitidas : 0;
    const metaFaturamento = todosGruposSelecionados ? kpisPbi.metaFaturamento : 0;

    return {
      metaFaturamento,
      faturamentoTotal: resumo.totalFaturamento + nfsEmitidas,
      nfsEmitidas,
      faturamentoCte: resumo.totalFaturamento,
      faturamentoFrota: resumo.totalFaturamento,
      faturamentoTerceiros: 0,
    };
  }, [kpisPbi, resumo.totalFaturamento, todosGruposSelecionados]);

  const groupCards = useMemo(() => {
    const map = new Map(gruposPbi.map(item => [normalizeGroup(item.grupo_pbi), parseValor(item.faturamento)]));
    const visibleGroups = MAIN_GROUPS.filter(group => gruposSelecionados.has(group));
    const max = Math.max(...visibleGroups.map(group => map.get(group) || 0), 1);

    return visibleGroups.map(group => {
      const value = map.get(group) || 0;
      const ratio = max > 0 ? value / max : 0;
      return {
        group,
        value,
        target: max,
        ratio,
        status: ratio >= 0.72 ? 'good' : ratio >= 0.35 ? 'warn' : 'low',
      };
    });
  }, [gruposPbi, gruposSelecionados]);

  const dailySeries = useMemo(() => {
    const daysInMonth = new Date(anoFiltro, mesFiltro + 1, 0).getDate();
    const byDay = new Map<number, { total: number; frota: number; terceiros: number }>();

    vendasFiltradas.forEach(item => {
      const parts = getDateParts(item.data_venda);
      if (!parts || parts.year !== anoFiltro || parts.month !== mesFiltro + 1) return;

      const group = normalizeGroup(item.grupo);
      const value = parseValor(item.valor_total);
      const current = byDay.get(parts.day) || { total: 0, frota: 0, terceiros: 0 };
      current.total += value;
      if (group === 'OUTROS' || group === 'TERCEIROS') {
        current.terceiros += value;
      } else {
        current.frota += value;
      }
      byDay.set(parts.day, current);
    });

    let totalAcc = 0;
    let frotaAcc = 0;
    let terceirosAcc = 0;
    const nfsTotal = kpisFiltrados.nfsEmitidas;
    const metaTotal = kpisFiltrados.metaFaturamento;

    return Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      const values = byDay.get(day) || { total: 0, frota: 0, terceiros: 0 };
      totalAcc += values.total;
      frotaAcc += values.frota;
      terceirosAcc += values.terceiros;

      return {
        day,
        total: totalAcc,
        frota: frotaAcc,
        terceiros: terceirosAcc,
        nfs: daysInMonth ? (nfsTotal / daysInMonth) * day : 0,
        meta: daysInMonth ? (metaTotal / daysInMonth) * day : 0,
      };
    });
  }, [anoFiltro, mesFiltro, vendasFiltradas, kpisFiltrados]);

  const previousDailyTotals = useMemo(() => {
    const byDay = new Map<number, number>();

    vendasMesAnteriorFiltradas.forEach(item => {
      const parts = getDateParts(item.data_venda);
      if (!parts) return;
      byDay.set(parts.day, (byDay.get(parts.day) || 0) + parseValor(item.valor_total));
    });

    const daysInMonth = new Date(anoFiltro, mesFiltro + 1, 0).getDate();
    let totalAcc = 0;

    return Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      totalAcc += byDay.get(day) || 0;
      return { day, total: totalAcc };
    });
  }, [anoFiltro, mesFiltro, vendasMesAnteriorFiltradas]);

  const hoveredPoint = useMemo(() => {
    if (!hoveredDay) return null;

    const current = dailySeries.find(item => item.day === hoveredDay);
    if (!current) return null;

    const lastMonth = previousDailyTotals.find(item => item.day === hoveredDay)?.total || 0;
    const comparison = lastMonth > 0 ? ((current.total - lastMonth) / lastMonth) * 100 : 0;

    return {
      ...current,
      lastMonth,
      comparison,
      tooltipLeft: dailySeries.length > 1 ? ((hoveredDay - 1) / (dailySeries.length - 1)) * 100 : 0,
    };
  }, [dailySeries, hoveredDay, previousDailyTotals]);

  const chartMax = useMemo(() => {
    return Math.max(
      1,
      ...dailySeries.flatMap(item => [item.total, item.frota, item.terceiros, item.nfs, item.meta])
    );
  }, [dailySeries]);

  const linePoints = (key: 'total' | 'frota' | 'terceiros' | 'nfs' | 'meta') => {
    const width = 1000;
    const height = 280;
    const xStep = dailySeries.length > 1 ? width / (dailySeries.length - 1) : width;
    return dailySeries
      .map((item, index) => {
        const x = index * xStep;
        const y = height - (Number(item[key]) / chartMax) * height;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(' ');
  };

  const toggleGroup = (group: string) => {
    setGruposSelecionados(prev => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  return (
    <div className="fat-report fat-dashboard-modern">
      <div className="fat-orange-filterbar">
        <label className="fat-filter-box">
          <span>ANO</span>
          <select value={anoFiltro} onChange={(e) => setAnoFiltro(Number(e.target.value))}>
            {years.map(year => <option key={year} value={year}>{year}</option>)}
          </select>
        </label>

        <label className="fat-filter-box">
          <span>MES</span>
          <select value={mesFiltro} onChange={(e) => setMesFiltro(Number(e.target.value))}>
            {MONTHS.map(month => <option key={month.value} value={month.value}>{month.label}</option>)}
          </select>
        </label>

        <div className="fat-group-filter">
          <span>GRUPOS</span>
          <div className="fat-group-pills">
            {MAIN_GROUPS.map(group => (
              <button
                key={group}
                className={gruposSelecionados.has(group) ? 'active' : ''}
                onClick={() => toggleGroup(group)}
                type="button"
              >
                {group}
              </button>
            ))}
          </div>
        </div>

        <div className="fat-filter-actions">
          <button className="fat-btn" onClick={carregar} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </button>
          <button className="fat-btn fat-btn-ghost" onClick={() => exportCsv(vendasFiltradas)} disabled={!vendasFiltradas.length}>
            CSV
          </button>
        </div>
      </div>

      {erro && <div className="fat-error">{erro}</div>}

      <div className="fat-gauge-grid">
        {groupCards.length === 0 ? (
          <div className="fat-empty-selection">Selecione pelo menos um grupo para visualizar o faturamento.</div>
        ) : groupCards.map(card => (
          <div className="fat-gauge-card" key={card.group}>
            <h4>FAT. GRUPO {card.group}</h4>
            <div
              className={`fat-gauge fat-gauge-${card.status}`}
              style={{ '--value': `${Math.min(card.ratio, 1) * 180}deg` } as React.CSSProperties}
            >
              <div className="fat-gauge-value">{card.value > 0 ? formatMillions(card.value) : '(Em branco)'}</div>
            </div>
            <div className="fat-gauge-scale">
              <span>0,000 Mi</span>
              <span>{formatMillions(card.target)}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="fat-group-value-grid">
        {groupCards.length === 0 ? (
          <div className="fat-empty-selection">Nenhum grupo selecionado.</div>
        ) : groupCards.map(card => (
          <div className="fat-group-value-card" key={card.group}>
            <span>{card.group}</span>
            <strong>{card.value > 0 ? formatCompactMoney(card.value) : '0'}</strong>
          </div>
        ))}
      </div>

      <div className="fat-main-grid">
        <div className="fat-summary-grid">
          <div className="fat-summary-card">
            <span>META DE FATURAMENTO</span>
            <strong>{formatMillions(kpisFiltrados.metaFaturamento)}</strong>
          </div>
          <div className="fat-summary-card">
            <span>FATURAMENTO TOTAL</span>
            <strong>{formatMillions(kpisFiltrados.faturamentoTotal)}</strong>
          </div>
          <div className="fat-summary-card">
            <span>NFS EMITIDAS</span>
            <strong>{formatMillions(kpisFiltrados.nfsEmitidas)}</strong>
          </div>
          <div className="fat-summary-card">
            <span>FATURAMENTO CTE</span>
            <strong>{formatMillions(kpisFiltrados.faturamentoCte)}</strong>
          </div>
          <div className="fat-summary-card">
            <span>FATURAMENTO FROTA</span>
            <strong>{formatMillions(kpisFiltrados.faturamentoFrota)}</strong>
          </div>
          <div className="fat-summary-card">
            <span>FATURAMENTO TERCEIROS</span>
            <strong>{formatMillions(kpisFiltrados.faturamentoTerceiros)}</strong>
          </div>
        </div>

        <div className="fat-line-panel">
          <div className="fat-line-title">
            <h4>{dashboardTitle || 'FATURAMENTO FKS'}</h4>
            <div className="fat-line-meta">
              {formatNumber(resumo.totalVendas)} CTEs | {formatNumber(resumo.clientesUnicos)} clientes | {formatNumber(resumo.bloqueados)} bloqueados
            </div>
          </div>
          <div className="fat-legend">
            <span><i className="meta" />META DE FAT. CLIENTE</span>
            <span><i className="nfs" />FAT. NFS</span>
            <span><i className="frota" />FAT. FROTA</span>
            <span><i className="terceiros" />FAT. TERCEIROS</span>
            <span><i className="total" />FAT. TOTAL</span>
          </div>
          <div className="fat-line-chart">
            <svg viewBox="0 0 1000 340" preserveAspectRatio="none" role="img" aria-label="Grafico de faturamento acumulado">
              <line x1="0" y1="280" x2="1000" y2="280" className="axis" />
              <line x1="0" y1="140" x2="1000" y2="140" className="grid" />
              <polyline points={linePoints('meta')} className="line meta" />
              <polyline points={linePoints('nfs')} className="line nfs" />
              <polyline points={linePoints('frota')} className="line frota" />
              <polyline points={linePoints('terceiros')} className="line terceiros" />
              <polyline points={linePoints('total')} className="line total" />
            </svg>
            <div
              className="fat-hover-layer"
              onMouseLeave={() => setHoveredDay(null)}
              style={{ gridTemplateColumns: `repeat(${dailySeries.length || 1}, minmax(0, 1fr))` }}
            >
              {dailySeries.map(item => (
                <button
                  aria-label={`Ver faturamento do dia ${item.day}`}
                  className="fat-hover-day"
                  key={item.day}
                  onMouseEnter={() => setHoveredDay(item.day)}
                  type="button"
                />
              ))}
            </div>
            {hoveredPoint && (
              <>
                <div
                  className="fat-hover-marker"
                  style={{
                    left: `calc(38px + (100% - 42px) * ${hoveredPoint.tooltipLeft / 100})`,
                    top: `${8 + 280 - (hoveredPoint.total / chartMax) * 280}px`,
                  }}
                />
                <div
                  className={`fat-chart-tooltip ${hoveredPoint.tooltipLeft > 68 ? 'align-right' : ''}`}
                  style={{ left: `${hoveredPoint.tooltipLeft}%` }}
                >
                  <strong className="fat-tooltip-day">{hoveredPoint.day}</strong>
                  <div className="fat-tooltip-row">
                    <i className="meta" />
                    <span>META DE FAT. CLIENTE</span>
                    <b>{formatTooltipMoney(hoveredPoint.meta)}</b>
                  </div>
                  <div className="fat-tooltip-row">
                    <i className="frota" />
                    <span>FAT. FROTA</span>
                    <b>{formatTooltipMoney(hoveredPoint.frota)}</b>
                  </div>
                  <div className="fat-tooltip-row">
                    <i className="total" />
                    <span>FAT. TOTAL</span>
                    <b>{formatTooltipMoney(hoveredPoint.total)}</b>
                  </div>
                  <div className="fat-tooltip-row no-dot">
                    <span>COMPARATIVO ULT. MES</span>
                    <b className={hoveredPoint.comparison >= 0 ? 'positive' : 'negative'}>
                      {hoveredPoint.comparison >= 0 ? '▲' : '▼'} {formatPercent(Math.abs(hoveredPoint.comparison))}%
                    </b>
                  </div>
                  <div className="fat-tooltip-row no-dot">
                    <span>FAT. ULTIMO MES</span>
                    <b>{formatTooltipMoney(hoveredPoint.lastMonth)}</b>
                  </div>
                </div>
              </>
            )}
            <div className="fat-chart-y max">{formatMillions(chartMax)}</div>
            <div className="fat-chart-y mid">{formatMillions(chartMax / 2)}</div>
            <div className="fat-chart-y zero">0 Mi</div>
            <div
              className="fat-day-axis"
              style={{ gridTemplateColumns: `repeat(${dailySeries.length || 1}, minmax(0, 1fr))` }}
            >
              {dailySeries.map(item => <span key={item.day}>{item.day}</span>)}
            </div>
            <div className="fat-axis-label">DATA DE EMISSAO</div>
          </div>
        </div>
      </div>

      <div className="fat-table-wrap">
        <h4>Detalhamento de faturamento</h4>
        <div className="fat-table-scroll">
          <table className="fat-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Grupo</th>
                <th>Cliente</th>
                <th>CTE</th>
                <th>Status</th>
                <th>Valor Total</th>
              </tr>
            </thead>
            <tbody>
              {vendasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan={6} className="fat-empty-cell">Nenhum faturamento encontrado.</td>
                </tr>
              ) : (
                vendasFiltradas.slice(0, 80).map((item, index) => (
                  <tr key={`${item.id || 0}-${index}`}>
                    <td>{formatDate(item.data_venda)}</td>
                    <td>{normalizeGroup(item.grupo)}</td>
                    <td>{item.cliente || '-'}</td>
                    <td>{item.cte || '-'}</td>
                    <td>{item.status || '-'}</td>
                    <td>{formatCurrency(parseValor(item.valor_total))}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FaturamentoMensalReport;
